import { useCallback, useEffect, useState } from 'react'
import { getWebinarSuggestion } from '@domain/features/webinar-suggestions/webinar-suggestions-feature'
import type { WebinarSuggestion } from '@domain/models/webinar-suggestions'

export function useWebinarSuggestion(suggestionId: string | undefined) {
  const [suggestion, setSuggestion] = useState<WebinarSuggestion | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState(0)

  const reload = useCallback(() => {
    setReloadTick((current) => current + 1)
  }, [])

  useEffect(() => {
    if (!suggestionId) {
      setSuggestion(null)
      setLoading(false)
      setError('Webinar suggestion id is missing.')
      return
    }

    const resolvedSuggestionId = suggestionId
    let cancelled = false

    async function load() {
      try {
        setLoading(true)
        setError(null)
        const result = await getWebinarSuggestion(resolvedSuggestionId)
        if (!cancelled) {
          setSuggestion(result)
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load webinar suggestion.',
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [reloadTick, suggestionId])

  return { suggestion, loading, error, reload }
}
